import { useAtomValue } from 'jotai';
import styled from 'styled-components';

import { typographyCss } from '@creditornot/cb-ingredients';
import { color, dimension } from '@creditornot/cb-ingredients/design-tokens';
import { Spinner } from '@creditornot/cb-progress';
import { ApiErrorNotification } from 'common/components/cookbook/ApiErrorNotification';
import { ContentContainer } from 'common/components/cookbook/ContentContainer';
import { Stack } from 'common/components/cookbook/Stack';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useSelectedCountry } from 'common/hooks/useSelectedCountry';
import { useSelectedCountryCodes } from 'common/hooks/useSelectedCountryCodes';
import { media } from 'common/media';

import { selectionRecommendationsRouteApi } from '..';
import { PerformanceMetric } from './PerformanceMetric';
import { usePerformanceOverview } from '../query';
import { selectedProductFamilyIdsAtom } from '../Recommendations/state';

const Title = styled.div`
  ${typographyCss.headline}

  padding-bottom: 16px;
`;

const Container = styled(Stack)`
  border: 1px solid ${color.border};
  border-radius: ${dimension.cornerRadiusXlarge};
  padding: ${dimension.spaceRegularXlarge};
`;

const Metrics = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 250px);
  gap: 16px;
  overflow-x: auto;
  overflow-y: hidden;

  @media ${media.sm} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

export const PerformanceOverview = () => {
  const f = useFormatMessage();
  const { error: countryError } = useSelectedCountry();

  const selectedCountryCodes = useSelectedCountryCodes();
  const productFamilyIds = useAtomValue(selectedProductFamilyIdsAtom);

  const countryCode = selectedCountryCodes[0];
  const { timePeriod = 'LAST_WEEK' } = selectionRecommendationsRouteApi.useSearch();

  const { data, error, isLoading } = usePerformanceOverview(countryCode, {
    period: timePeriod ?? 'LAST_WEEK',
    productFamilyIds,
  });

  const overviews = data?.overviews ?? [];

  const metrics = [
    {
      metric: overviews.find((overview) => overview.metricType === 'SELECTION_COUNT'),
      label: f('selection_recommendations.performance_overview_selection_count'),
    },
    {
      metric: overviews.find((overview) => overview.metricType === 'AVERAGE_BASKET_VALUE'),
      label: f('selection_recommendations.performance_overview_avb'),
    },
    {
      metric: overviews.find((overview) => overview.metricType === 'SHRINKAGE'),
      label: f('selection_recommendations.performance_overview_shrinkage'),
    },
    {
      metric: overviews.find((overview) => overview.metricType === 'PURE_PRODUCT_MARGIN'),
      label: f('selection_recommendations.performance_overview_margin'),
    },
  ];

  return (
    <ContentContainer>
      <Title>{f('selection_recommendations.performance_overview')}</Title>
      <ApiErrorNotification error={error || countryError} />
      <Metrics>
        {metrics.map(({ metric, label }, index) => (
          <Container key={index} gap="regularMedium" direction="row" justifyContent="center">
            {isLoading ? (
              <Spinner size="small" />
            ) : (
              <PerformanceMetric
                key={index}
                metric={metric}
                label={label}
                timePeriod={timePeriod}
              />
            )}
          </Container>
        ))}
      </Metrics>
    </ContentContainer>
  );
};
